/**
 * Receives content shared into Enough via the native OS share sheet and processes it.
 *
 * DEDICATED channel, separate from `useShareReceiver` (`appUrlOpen` deep links). External
 * product URLs arrive here via the `ShareReceiver` plugin — never the deep-link path.
 *
 * iOS mini-app: when authed, the extension writes directly via
 * `enough_cart_vault_add_from_share`. Items without auth/network fall back to the
 * App Group queue, drained here on foreground.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import ShareReceiver, { type PendingShareItem } from '../plugins/ShareReceiver';
import { extractUrlFromText, parseRetailerUrl } from '../utils/retailerUrlParser';
import { buildUrlFirstTitle } from '../utils/urlFirstProductPreview';
import { addSharedVaultItem, findVaultItemByUrl } from '../services/cartCompanionService';
import { useFeatureFlags } from '../context/RemoteConfigContext';
import { useAuth } from '../context/AuthContext';
import { logError } from '@/lib/log';

/** True if the URL points to the app itself (internal deep link, not a shared product). */
function isInternalLink(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname.endsWith('enough.app');
  } catch {
    return false;
  }
}

function parsePrice(price?: string): number | null {
  if (!price) return null;
  const n = parseFloat(price.replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
}

export function useShareTargetReceiver() {
  const navigate = useNavigate();
  const { cartCompanionEnabled } = useFeatureFlags();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const processItem = async (item: PendingShareItem): Promise<'created' | 'preview' | 'skip'> => {
      const url = extractUrlFromText(item.url) ?? item.url;
      if (!url.startsWith('http')) return 'skip';

      // Internal links route as normal deep links — never into the vault.
      if (isInternalLink(url)) {
        try {
          navigate(new URL(url).pathname || '/');
        } catch { /* ignore */ }
        return 'skip';
      }

      if (!cartCompanionEnabled) return 'skip';

      // No duration chosen (Android) → in-app preview where the user picks one.
      if (!item.periodDays) return 'preview';

      // Mini-app: the user already chose price + duration → create the hold directly.
      const uid = currentUser?.uid;
      if (!uid) return 'preview';
      const parsed = parseRetailerUrl(url);
      if (!parsed) return 'skip';
      try {
        const existing = await findVaultItemByUrl(uid, parsed.originalUrl);
        if (existing) return 'skip'; // already in the vault — don't duplicate
        await addSharedVaultItem(uid, {
          title: buildUrlFirstTitle(parsed),
          price: parsePrice(item.price),
          url: parsed.originalUrl,
          retailer: parsed.retailer,
          asin: parsed.asin,
          coolingOffDays: item.periodDays,
          sharedAtISO: item.sharedAt ?? new Date().toISOString(),
        });
        return 'created';
      } catch (err) {
        logError(err, { context: 'share_target_create_shared_item' });
        return 'skip';
      }
    };

    const drain = async () => {
      if (!currentUser?.uid) return; // wait until authenticated; queue persists in App Group
      let result: { items: PendingShareItem[] };
      try {
        result = await ShareReceiver.getPendingShares();
      } catch {
        return;
      }
      if (!result?.items?.length) return;

      let created = 0;
      let lastPreviewUrl: string | null = null;
      for (const item of result.items) {
        const outcome = await processItem(item);
        if (outcome === 'created') created++;
        else if (outcome === 'preview') lastPreviewUrl = extractUrlFromText(item.url) ?? item.url;
      }

      // Route the user to the result. Created holds → the vault. A no-duration item
      // (Android) → its preview so they can set a duration.
      if (lastPreviewUrl) {
        navigate(`/pause?tab=pre-purchase&shared=${encodeURIComponent(lastPreviewUrl)}`);
      } else if (created > 0) {
        navigate('/pause?tab=pre-purchase');
      }
    };

    drain();

    const sharePromise = ShareReceiver.addListener('shareReceived', () => { void drain(); });
    const statePromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void drain();
    });

    return () => {
      sharePromise.then((l) => l.remove()).catch(() => {});
      statePromise.then((l) => l.remove()).catch(() => {});
    };
  }, [navigate, cartCompanionEnabled, currentUser?.uid]);
}
